import { error } from "../../logger";
import RedisConnectionPool from "../../util/ConnectionPool";
import { Cache } from "../types/";

export class Redis implements Cache {
  private logError(method: string, keyOrPattern: string, err: unknown) {
    error(`Error in ${method} - Key/Pattern: ${keyOrPattern}\n`, {
      operation: method,
      err,
    });
  }

  async del(key: string): Promise<void> {
    const conn = await RedisConnectionPool.GetConnection();
    try {
      await conn.del(key);
    } catch (error) {
      this.logError("del", key, error);
    } finally {
      RedisConnectionPool.releaseConnection(conn);
    }
  }

  async get<T>(key: string): Promise<T | null> {
    const conn = await RedisConnectionPool.GetConnection();
    try {
      const data = await conn.get(key);
      return data ? (JSON.parse(data) as T) : null;
    } catch (err) {
      this.logError("get", key, error);
      return null;
    } finally {
      RedisConnectionPool.releaseConnection(conn);
    }
  }

  async set<T>(key: string, obj: T, ttl: number = 86400): Promise<void> {
    if (!obj) return;
    const conn = await RedisConnectionPool.GetConnection();
    try {
      await conn.setEx(key, ttl, JSON.stringify(obj));
    } catch (error) {
      this.logError("set", key, error);
    } finally {
      RedisConnectionPool.releaseConnection(conn);
    }
  }

  async delPattern(pattern: string): Promise<void> {
    const conn = await RedisConnectionPool.GetConnection();
    try {
      const keys = await conn.keys(pattern);
      if (keys.length) await conn.del(keys);
    } catch (error) {
      this.logError("delPattern", pattern, error);
    } finally {
      RedisConnectionPool.releaseConnection(conn);
    }
  }

  async getByPattern<T>(pattern: string): Promise<T[]> {
    const conn = await RedisConnectionPool.GetConnection();
    try {
      let cursor: number = 0;
      const keys = [];
      const conn = await RedisConnectionPool.GetConnection();
      do {
        const reply = await conn.scan(cursor, {
          MATCH: pattern,
          COUNT: 100,
        });
        cursor = reply.cursor;
        keys.push(...reply.keys);
      } while (cursor !== 0);

      const values: T[] = [];

      for (const key of keys) {
        const value = (await conn.get(key)) as T;
        values.push(value);
      }

      RedisConnectionPool.releaseConnection(conn);
      return values;
    } catch (error) {
      this.logError("getByPattern", pattern, error);
      return [];
    } finally {
      RedisConnectionPool.releaseConnection(conn);
    }
  }
}
